import { useState } from "react";
import { NurseLayout } from "@/components/layout/nurse-layout";
import {
  Hospital,
  SupplyCompany,
  useListHospitals,
  useListSupplyCompanies,
  getListHospitalsQueryKey,
  getListSupplyCompaniesQueryKey,
} from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import {
  Building2,
  Phone,
  Mail,
  ExternalLink,
  Clock,
  MapPin,
  Search,
  ChevronDown,
  ChevronUp,
  Package,
  BookUser,
} from "lucide-react";

type UKRegion =
  | "London"
  | "South East"
  | "South West"
  | "Midlands"
  | "East of England"
  | "North West"
  | "North East and Yorkshire"
  | "East Midlands"
  | "West Midlands"
  | "Wales"
  | "Scotland"
  | "Northern Ireland";

const UK_REGIONS: UKRegion[] = [
  "London",
  "South East",
  "South West",
  "East of England",
  "East Midlands",
  "West Midlands",
  "Midlands",
  "North West",
  "North East and Yorkshire",
  "Wales",
  "Scotland",
  "Northern Ireland",
];

const COMPANY_TYPE_LABELS: Record<string, string> = {
  homecare: "Homecare",
  pharmacy: "Pharmacy",
  logistics: "Logistics",
  other: "Other",
};

const COMPANY_TYPE_COLORS: Record<string, string> = {
  homecare: "bg-teal-100 text-teal-700 border-teal-200",
  pharmacy: "bg-blue-100 text-blue-700 border-blue-200",
  logistics: "bg-amber-100 text-amber-700 border-amber-200",
  other: "bg-slate-100 text-slate-600 border-slate-200",
};

type Tab = "hospitals" | "supply";

function HospitalCard({ hospital }: { hospital: Hospital }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200">
              {hospital.region}
            </span>
          </div>
          <h3 className="text-sm font-bold text-slate-900 leading-snug">{hospital.nhsTrustName}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{hospital.name}</p>
        </div>
        <Building2 className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
      </div>

      {hospital.city && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span>{hospital.city}{hospital.postcode ? `, ${hospital.postcode}` : ""}</span>
        </div>
      )}

      {hospital.adviceLinePhone && (
        <div className="bg-teal-50 border border-teal-100 rounded-xl p-3 space-y-1.5">
          <p className="text-xs font-semibold text-teal-800">Rheumatology Advice Line</p>
          <a
            href={`tel:${hospital.adviceLinePhone.replace(/\s/g, "")}`}
            className="flex items-center gap-2 text-sm font-bold text-teal-700 hover:text-teal-900 transition-colors"
          >
            <Phone className="h-4 w-4" />
            {hospital.adviceLinePhone}
          </a>
          {hospital.adviceLineHours && (
            <div className="flex items-center gap-1.5 text-xs text-teal-600">
              <Clock className="h-3 w-3 shrink-0" />
              <span>{hospital.adviceLineHours}</span>
            </div>
          )}
          {hospital.adviceLineEmail && (
            <a
              href={`mailto:${hospital.adviceLineEmail}`}
              className="flex items-center gap-1.5 text-xs text-teal-600 hover:text-teal-800 transition-colors truncate"
            >
              <Mail className="h-3 w-3 shrink-0" />
              {hospital.adviceLineEmail}
            </a>
          )}
        </div>
      )}

      {!hospital.adviceLinePhone && hospital.phone && (
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <a href={`tel:${hospital.phone.replace(/\s/g, "")}`} className="hover:text-teal-700 transition-colors">
            {hospital.phone}
          </a>
        </div>
      )}

      {hospital.notes && (
        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">{hospital.notes}</p>
      )}

      {hospital.website && (
        <a
          href={hospital.website}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-600 hover:text-teal-800 transition-colors"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Visit website
        </a>
      )}
    </div>
  );
}

function SupplyCompanyCard({ company }: { company: SupplyCompany }) {
  const [expanded, setExpanded] = useState(false);
  const hasContacts = company.contacts && company.contacts.length > 0;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col hover:shadow-md transition-shadow">
      <div className="p-5 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${COMPANY_TYPE_COLORS[company.type] ?? "bg-slate-100 text-slate-600 border-slate-200"}`}>
                {COMPANY_TYPE_LABELS[company.type] ?? company.type}
              </span>
            </div>
            <h3 className="text-sm font-bold text-slate-900">{company.name}</h3>
          </div>
          <Package className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
        </div>

        {company.description && (
          <p className="text-xs text-slate-500 leading-relaxed line-clamp-3">{company.description}</p>
        )}

        <div className="space-y-1.5">
          {company.nurseLinePhone && (
            <div className="bg-teal-50 border border-teal-100 rounded-xl p-3 space-y-1.5">
              <p className="text-xs font-semibold text-teal-800">Nurse / Referral Line</p>
              <a
                href={`tel:${company.nurseLinePhone.replace(/\s/g, "")}`}
                className="flex items-center gap-2 text-sm font-bold text-teal-700 hover:text-teal-900 transition-colors"
              >
                <Phone className="h-4 w-4" />
                {company.nurseLinePhone}
              </a>
              {company.nurseLineHours && (
                <div className="flex items-center gap-1.5 text-xs text-teal-600">
                  <Clock className="h-3 w-3 shrink-0" />
                  <span>{company.nurseLineHours}</span>
                </div>
              )}
            </div>
          )}

          {company.mainPhone && !company.nurseLinePhone && (
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <a href={`tel:${company.mainPhone.replace(/\s/g, "")}`} className="hover:text-teal-700 transition-colors">
                {company.mainPhone}
              </a>
            </div>
          )}

          {company.mainEmail && (
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <a href={`mailto:${company.mainEmail}`} className="hover:text-teal-700 transition-colors truncate">
                {company.mainEmail}
              </a>
            </div>
          )}
        </div>

        {company.notes && (
          <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">{company.notes}</p>
        )}

        <div className="flex items-center justify-between gap-3 pt-1">
          {company.website ? (
            <a
              href={company.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-600 hover:text-teal-800 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Visit website
            </a>
          ) : <span />}

          {hasContacts && (
            <button
              onClick={() => setExpanded(p => !p)}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
            >
              Regional contacts ({company.contacts.length})
              {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>

      {expanded && hasContacts && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {company.contacts.map(contact => (
            <div key={contact.id} className="px-5 py-3 flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-slate-700">{contact.region}</span>
                {contact.contactName && (
                  <span className="text-xs text-slate-500">{contact.contactName}</span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {contact.phone && (
                  <a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-xs text-teal-600 hover:text-teal-800 transition-colors">
                    <Phone className="h-3 w-3 shrink-0" />
                    {contact.phone}
                  </a>
                )}
                {contact.email && (
                  <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 text-xs text-teal-600 hover:text-teal-800 transition-colors truncate">
                    <Mail className="h-3 w-3 shrink-0" />
                    {contact.email}
                  </a>
                )}
              </div>
              {contact.notes && (
                <p className="text-xs text-slate-400 mt-0.5">{contact.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NurseDirectory() {
  const [tab, setTab] = useState<Tab>("hospitals");
  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState<UKRegion | "">("");

  const debouncedSearch = search.trim().length >= 2 ? search.trim() : undefined;

  const hospitalParams = {
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(regionFilter ? { region: regionFilter } : {}),
  };

  const supplyParams = {
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  };

  const { data: hospitals, isLoading: hospitalsLoading, error: hospitalsError } = useListHospitals(
    hospitalParams,
    { query: { enabled: tab === "hospitals", queryKey: getListHospitalsQueryKey(hospitalParams) } },
  );

  const { data: supplyCompanies, isLoading: supplyLoading, error: supplyError } = useListSupplyCompanies(
    supplyParams,
    { query: { enabled: tab === "supply", queryKey: getListSupplyCompaniesQueryKey(supplyParams) } },
  );

  const isLoading = tab === "hospitals" ? hospitalsLoading : supplyLoading;
  const error = tab === "hospitals" ? hospitalsError : supplyError;

  return (
    <NurseLayout title="Directory">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-teal-100 p-2.5 rounded-xl">
            <BookUser className="h-6 w-6 text-teal-700" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Advice Line Directory</h2>
        </div>
        <p className="text-slate-600 max-w-2xl">
          NHS rheumatology advice line contacts across the UK, plus homecare supply company regional directories for biologics and specialist medicines.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => { setTab("hospitals"); setSearch(""); setRegionFilter(""); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "hospitals" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <Building2 className="h-4 w-4" />
            NHS Hospitals
          </button>
          <button
            onClick={() => { setTab("supply"); setSearch(""); setRegionFilter(""); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "supply" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <Package className="h-4 w-4" />
            Supply Companies
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={tab === "hospitals" ? "Search hospitals, trusts, cities…" : "Search companies…"}
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {tab === "hospitals" && (
          <select
            value={regionFilter}
            onChange={e => setRegionFilter(e.target.value as UKRegion | "")}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-slate-700"
          >
            <option value="">All regions</option>
            {UK_REGIONS.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        )}
      </div>

      {isLoading ? (
        <LoadingScreen />
      ) : error ? (
        <div className="text-center py-16 text-slate-500">
          <Building2 className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">Unable to load directory</p>
          <p className="text-sm mt-1">Please try again later.</p>
        </div>
      ) : tab === "hospitals" ? (
        <>
          {!hospitals || hospitals.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Building2 className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No hospitals found</p>
              <p className="text-sm mt-1">Try adjusting your search or region filter.</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-400 mb-4">{hospitals.length} hospital{hospitals.length !== 1 ? "s" : ""} found</p>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {hospitals.map(h => (
                  <HospitalCard key={h.id} hospital={h} />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          {!supplyCompanies || supplyCompanies.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Package className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No supply companies found</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-400 mb-4">{supplyCompanies.length} compan{supplyCompanies.length !== 1 ? "ies" : "y"} found</p>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {supplyCompanies.map(c => (
                  <SupplyCompanyCard key={c.id} company={c} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </NurseLayout>
  );
}
